import { FetchPolicyError, fetchWithPolicy } from './fetchPolicy'

type ApiErrorMessageMapper = (status: number, payload: unknown) => string
type ApiErrorFactory = (status: number, message: string) => Error

export const API_BASE = process.env.NEXT_PUBLIC_API_URL || '/api'

const toHeaderRecord = (headers?: HeadersInit): Record<string, string> => {
  if (!headers) return {}

  if (headers instanceof Headers) {
    return Object.fromEntries(headers.entries())
  }

  if (Array.isArray(headers)) {
    return Object.fromEntries(headers)
  }

  return { ...headers }
}

const mergeHeaders = (
  defaults: Record<string, string>,
  headers?: HeadersInit,
): Record<string, string> => ({
  ...defaults,
  ...toHeaderRecord(headers),
})

export const parseJsonPayload = async (response: Response): Promise<unknown> => {
  const text = await response.text()
  if (!text) return null
  try {
    return JSON.parse(text)
  } catch {
    return null
  }
}

export const requestApiJson = async <T>({
  path,
  init,
  defaultHeaders = { 'Content-Type': 'application/json' },
  toErrorMessage,
  createError,
}: {
  path: string
  init?: RequestInit
  defaultHeaders?: Record<string, string>
  toErrorMessage: ApiErrorMessageMapper
  createError: ApiErrorFactory
}): Promise<T> => {
  let response: Response
  try {
    response = await fetchWithPolicy({
      request: `${API_BASE}${path}`,
      init: {
        ...init,
        headers: mergeHeaders(defaultHeaders, init?.headers),
      },
    })
  } catch (error) {
    if (error instanceof FetchPolicyError) {
      const status = error.kind === 'timeout' ? 504 : error.kind === 'aborted' ? 499 : 503
      throw createError(status, toErrorMessage(status, null))
    }
    throw error
  }

  const payload = await parseJsonPayload(response)
  if (!response.ok) {
    throw createError(response.status, toErrorMessage(response.status, payload))
  }

  return payload as T
}

export const requestApiBlob = async ({
  url,
  init,
  defaultHeaders = {},
  toErrorMessage,
  createError,
}: {
  url: string
  init?: RequestInit
  defaultHeaders?: Record<string, string>
  toErrorMessage: ApiErrorMessageMapper
  createError: ApiErrorFactory
}): Promise<Blob> => {
  let response: Response
  try {
    response = await fetchWithPolicy({
      request: url,
      init: {
        ...init,
        headers: mergeHeaders(defaultHeaders, init?.headers),
      },
    })
  } catch (error) {
    if (error instanceof FetchPolicyError) {
      const status = error.kind === 'timeout' ? 504 : error.kind === 'aborted' ? 499 : 503
      throw createError(status, toErrorMessage(status, null))
    }
    throw error
  }

  if (!response.ok) {
    const payload = await parseJsonPayload(response)
    throw createError(response.status, toErrorMessage(response.status, payload))
  }

  return response.blob()
}
